import React, { useEffect, useState, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  getFirestore,
  doc,
  onSnapshot,
  collection,
  query,
  where,
  limit,
  getDocs,
  getDoc,
  updateDoc
} from "firebase/firestore";
import app from "../../firebase";
import { clearEnrolledSession } from "../../auth/roleSession";
import { getActiveSemester, getAnnouncementSchoolYear, useCalendarClock } from "../../utils/announcementDates";

export default function ClassSchedule({ studentId }) {
  const todayKey = useCalendarClock();
  // Layout constants - unified design system
  const SIDEBAR_WIDTH = 240;
  const HEADER_HEIGHT = 70;
  const PAGE_PADDING = 32;
  const CONTENT_MAX = 1100;

  const location = useLocation();
  const navigate = useNavigate();

  // restore page state used by the component
  const [courses, setCourses] = useState([]);
  const [enrichedCourses, setEnrichedCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [student, setStudent] = useState(null);
  const [resolvedRef, setResolvedRef] = useState(null);
  const [selectedSchoolYear, setSelectedSchoolYear] = useState("");
  const [selectedSemester, setSelectedSemester] = useState("");
  const [currentSchoolYear, setCurrentSchoolYear] = useState("");
  const [currentSemester, setCurrentSemester] = useState("");
  const [announcementData, setAnnouncementData] = useState(null);
  const [scrollOffset, setScrollOffset] = useState(0);
  const [lastScrollTop, setLastScrollTop] = useState(0);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);

  // fetch/subscribe to student schedules — resolves sid from prop or navigation state
  const sidFromLocation = location.state?.student?.studentId || location.state?.student?.id;
  
  // Try to load student from sessionStorage if not in location state
  let enrolledStudentFromStorage = null;
  if (!sidFromLocation) {
    try {
      const stored = localStorage.getItem("enrolledStudentData");
      if (stored) {
        enrolledStudentFromStorage = JSON.parse(stored);
      }
    } catch (e) {
      console.error("Failed to parse enrolledStudentData from sessionStorage:", e);
    }
  }
  
  const resolvedStudentId = studentId || sidFromLocation || enrolledStudentFromStorage?.id;


  // Track the selected announcement settings and refresh the active term as the date changes.
  useEffect(() => {
    const announcementRef = doc(getFirestore(app), "systemSettings", "announcement");
    return onSnapshot(announcementRef, snapshot => {
      setAnnouncementData(snapshot.exists() ? snapshot.data() : null);
    }, error => console.error("Failed to listen to announcement settings:", error));
  }, []);

  useEffect(() => {
    const activeSemester = getActiveSemester(announcementData || {}, todayKey);
    setCurrentSemester(activeSemester === "first" ? "First Semester" : activeSemester === "second" ? "Second Semester" : "");
    setCurrentSchoolYear(activeSemester ? getAnnouncementSchoolYear(announcementData, activeSemester) : "");
  }, [announcementData, todayKey]);

  useEffect(() => {
    if (!resolvedStudentId) return;
    setLoading(true);
    setError("");

    const db = getFirestore(app);
    let unsub = null;

    (async () => {
      try {
        // 1) try as direct document id
        const directRef = doc(db, "students", String(resolvedStudentId).trim());
        const directSnap = await getDoc(directRef);
        let docRef = null;

        if (directSnap.exists()) {
          docRef = directRef;
        } else {
          // 2) try query by studentId field (for enrolled students)
          const q1 = query(collection(db, "students"), where("studentId", "==", String(resolvedStudentId).trim()), limit(1));
          const qSnap1 = await getDocs(q1);
          if (!qSnap1.empty) {
            docRef = doc(db, "students", qSnap1.docs[0].id);
          } else {
            // 3) try query by idNumber field (for pre-registration students)
            const q2 = query(collection(db, "students"), where("idNumber", "==", String(resolvedStudentId).trim()), limit(1));
            const qSnap2 = await getDocs(q2);
            if (!qSnap2.empty) {
              docRef = doc(db, "students", qSnap2.docs[0].id);
            }
          }
        }

        if (!docRef) {
          setCourses([]);
          setError("Student not found.");
          setLoading(false);
          return;
        }

        setResolvedRef(docRef);

        unsub = onSnapshot(docRef, (snap) => {
          if (snap.exists()) {
            const data = snap.data();
            setStudent({ id: snap.id, ...data });
          } else {
            setStudent(null);
            setError("Student not found.");
          }
          setLoading(false);
        }, (err) => {
          setError("Failed to fetch class schedule.");
          setLoading(false);
        });
      } catch (err) {
        console.error("ClassSchedule resolve error:", err);
        setError("Failed to load student.");
        setLoading(false);
      }
    })();

    return () => {
      if (unsub) unsub();
    };
  }, [resolvedStudentId]);

  // Get unique school years and semesters from termSchedules (memoized)
  const allSchedules = useMemo(() => {
    const schedules = [];
    if (student?.termSchedules && typeof student.termSchedules === 'object') {
      Object.entries(student.termSchedules).forEach(([termKey, schedArray]) => {
        if (Array.isArray(schedArray)) {
          schedules.push(...schedArray);
        }
      });
    }
    return schedules;
  }, [student?.termSchedules]);

  const schoolYears = useMemo(() => {
    return allSchedules.length > 0
      ? [...new Set(allSchedules.map(s => s.schoolYear).filter(Boolean))]
      : [];
  }, [allSchedules]);

  const semesters = useMemo(() => {
    return allSchedules.length > 0
      ? [...new Set(allSchedules.map(s => s.semester).filter(Boolean))]
      : [];
  }, [allSchedules]);

  // Auto-select current term or first option
  useEffect(() => {
    if (currentSchoolYear && schoolYears.includes(currentSchoolYear) && !selectedSchoolYear) {
      setSelectedSchoolYear(currentSchoolYear);
    } else if (schoolYears.length > 0 && !selectedSchoolYear) {
      setSelectedSchoolYear(schoolYears[0]);
    }
    
    if (currentSemester && semesters.includes(currentSemester) && !selectedSemester) {
      setSelectedSemester(currentSemester);
    } else if (semesters.length > 0 && !selectedSemester) {
      setSelectedSemester(semesters[0]);
    }
  }, [schoolYears, semesters, selectedSchoolYear, selectedSemester, currentSchoolYear, currentSemester]);

  // Filter courses based on selected term (memoized)
  const filteredCourses = useMemo(() => {
    if (selectedSchoolYear && selectedSemester && allSchedules.length > 0) {
      return allSchedules.filter(schedule => {
        return schedule.schoolYear === selectedSchoolYear && schedule.semester === selectedSemester;
      });
    }
    return [];
  }, [allSchedules, selectedSchoolYear, selectedSemester]);

  // Update courses state when filtered courses change
  useEffect(() => {
    setCourses(filteredCourses);
  }, [filteredCourses]);

  // Enrich courses with `profName` resolved from `users` or prettified fallback
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const db = getFirestore(app);
        const resolved = [];
        for (const c of courses || []) {
          let profName = null;
          const profVal = c.prof;

          if (typeof profVal === 'string' && profVal.includes('@')) {
            try {
              const q = query(collection(db, 'users'), where('email', '==', String(profVal).trim()), limit(1));
              const snap = await getDocs(q);
              if (!snap.empty) {
                const u = snap.docs[0].data() || {};
                profName = u.name || (u.firstName ? `${u.firstName} ${u.lastName || ''}`.trim() : u.email);
              }
            } catch (err) {}
          }

          if (!profName && (c.professorId || (typeof profVal === 'string' && profVal && !profVal.includes('@')))) {
            const candidateId = c.professorId || String(profVal);
            try {
              const userSnap = await getDoc(doc(db, 'users', String(candidateId)));
              if (userSnap.exists()) {
                const u = userSnap.data() || {};
                profName = u.name || (u.firstName ? `${u.firstName} ${u.lastName || ''}`.trim() : u.email || null);
              }
            } catch (err) {}
          }

          if (!profName && typeof profVal === 'string' && profVal.includes('@')) {
            const local = String(profVal).split('@')[0];
            profName = local.split(/[^A-Za-z0-9]+/).filter(Boolean).map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(' ');
          }

          resolved.push({ ...c, profName: profName || profVal });
        }
        if (!cancelled) setEnrichedCourses(resolved);
      } catch (e) {
        if (!cancelled) setEnrichedCourses(courses);
      }
    })();
    return () => { cancelled = true; };
  }, [courses]);

  // header student derived from live state or navigation state
  const headerStudent = student || location.state?.student || {};
  const fullName = `${headerStudent?.firstName || ""} ${headerStudent?.lastName || ""}`.trim();
  const profilePicUrl = headerStudent?.profilePicUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(fullName || "Enrolled")}`;

  const handleProfile = (action) => {
    setProfileMenuOpen(false);
    if (action === "logout") {
      clearEnrolledSession();
      navigate("/enrolled/signin");
      return;
    }
  };

  const navItems = [
    { key: "home", label: "Home", path: "/enrolled/dashboard" },
    { key: "pre", label: "Pre-Registration", path: "/enrolled/pre-registration" },
    { key: "curriculum", label: "Curriculum Overview", path: "/enrolled/curriculum-overview" },
    { key: "schedule", label: "Class Schedule", path: "/enrolled/class-schedule" },
    { key: "final", label: "COR", path: "/enrolled/cor" },
  ];

  // Handle scroll for hide-on-scroll behavior
  useEffect(() => {
    const mainElement = document.querySelector('[data-scroll-container]');
    if (!mainElement) return;

    const handleScroll = () => {
      const scrollTop = mainElement.scrollTop;
      if (scrollTop > lastScrollTop) {
        // Scrolling down
        setScrollOffset(Math.min(scrollTop - lastScrollTop, HEADER_HEIGHT + SIDEBAR_WIDTH));
      } else {
        // Scrolling up
        setScrollOffset(Math.max(scrollTop - lastScrollTop, -(HEADER_HEIGHT + SIDEBAR_WIDTH)));
      }
      setLastScrollTop(scrollTop);
    };

    mainElement.addEventListener('scroll', handleScroll);
    return () => mainElement.removeEventListener('scroll', handleScroll);
  }, [lastScrollTop]);

  // Replace ad-hoc layout return with consistent layout used by dashboard
  return (
    <div style={{ display: "flex", minHeight: "100vh", fontFamily: "Inter, Arial, sans-serif", background: "#efefef" }}>
      <aside style={{ width: SIDEBAR_WIDTH, background: "#0f5f3b", color: "#fff", display: "flex", flexDirection: "column", position: "fixed", height: "100vh", left: 0, top: 0, transform: `translateY(${Math.max(-scrollOffset, -HEADER_HEIGHT)}px)`, transition: "transform 0.3s ease", zIndex: 1000 }}>
        <div style={{ padding: "28px 24px", borderBottom: "1px solid rgba(255,255,255,0.1)" }}>
          <div style={{ fontSize: 24, fontWeight: 800, letterSpacing: 1 }}>EnrollEase</div>
        </div>
        <nav style={{ paddingTop: 24, flex: 1 }}>
          {navItems.map((n) => {
            const isActive = window.location.pathname.startsWith(n.path);
            return (
              <div key={n.key} onClick={() => navigate(n.path, { state: { student: headerStudent } })} style={{ padding: "14px 24px", margin: "4px 16px", borderRadius: 8, cursor: "pointer", background: isActive ? "#ff6f2e" : "transparent", color: "#fff", fontWeight: 600, fontSize: 15, transition: "all 0.2s", boxShadow: isActive ? "0 2px 8px rgba(255,111,46,0.3)" : "none", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{n.label}</div>
            );
          })}
        </nav>
      </aside>

      <div style={{ flex: 1, display: "flex", flexDirection: "column", marginLeft: SIDEBAR_WIDTH }}>
        <header style={{ height: HEADER_HEIGHT, background: "#fff", display: "flex", alignItems: "center", justifyContent: "space-between", padding: `0 ${PAGE_PADDING}px`, boxSizing: "border-box", borderBottom: "1px solid #e0e0e0", boxShadow: "0 1px 3px rgba(0,0,0,0.05)", position: "sticky", top: 0, transform: `translateY(${-Math.min(scrollOffset, HEADER_HEIGHT)}px)`, transition: "transform 0.3s ease", zIndex: 999, position: "relative" }}>
          <div style={{ color: "#0f5f3b", fontWeight: 700, fontSize: 20 }}>Class Schedule</div>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <div style={{ textAlign: "right" }}>
              <div style={{ color: "#0f5f3b", fontWeight: 600, fontSize: 15 }}>{fullName}</div>
            </div>
            <img
              src={profilePicUrl}
              alt="Profile"
              style={{ width: 48, height: 48, borderRadius: "50%", objectFit: "cover", cursor: "pointer", border: "3px solid #ff6f2e" }}
              onClick={() => setProfileMenuOpen(v => !v)}
            />
            {profileMenuOpen && (
              <div style={{ position: "absolute", top: HEADER_HEIGHT - 4, right: PAGE_PADDING, background: "#fff", borderRadius: 8, boxShadow: "0 4px 16px rgba(0,0,0,0.13)", minWidth: 180, padding: "10px 0", zIndex: 100 }}>
                <div style={{ padding: "10px 24px", cursor: "pointer", color: "#ff6f2e", fontWeight: 600 }} onClick={() => handleProfile("logout")}>Log out</div>
              </div>
            )}
          </div>
        </header>

        <main style={{ flex: 1, padding: PAGE_PADDING, boxSizing: "border-box", overflowY: "auto", marginTop: 0 }} data-scroll-container>
          <div style={{ maxWidth: "100%", margin: "0 auto", height: "100%" }}>
            {/* Filter Section */}
            {schoolYears.length > 0 && (
              <div style={{ background: "#fff", borderRadius: 8, padding: "24px", marginBottom: "24px", boxShadow: "0 1px 3px rgba(0,0,0,0.1)" }}>
                <div style={{ display: "flex", gap: "24px", alignItems: "flex-end" }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: "block", fontWeight: 600, marginBottom: "8px", color: "#222" }}>
                      School Year: <span style={{ color: "red" }}>*</span>
                    </label>
                    <select
                      value={selectedSchoolYear}
                      onChange={(e) => setSelectedSchoolYear(e.target.value)}
                      style={{
                        width: "100%",
                        padding: "10px 12px",
                        borderRadius: "6px",
                        border: "1px solid #ccc",
                        fontSize: "14px",
                        fontFamily: "inherit"
                      }}
                    >
                      {schoolYears.map((year) => (
                        <option key={year} value={year}>
                          {year}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div style={{ flex: 1 }}>
                    <label style={{ display: "block", fontWeight: 600, marginBottom: "8px", color: "#222" }}>
                      Semester: <span style={{ color: "red" }}>*</span>
                    </label>
                    <select
                      value={selectedSemester}
                      onChange={(e) => setSelectedSemester(e.target.value)}
                      style={{
                        width: "100%",
                        padding: "10px 12px",
                        borderRadius: "6px",
                        border: "1px solid #ccc",
                        fontSize: "14px",
                        fontFamily: "inherit"
                      }}
                    >
                      {semesters.map((sem) => (
                        <option key={sem} value={sem}>
                          {sem}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            )}

            <div style={{ background: "#f8fafb", borderRadius: 8, boxShadow: "0 1px 2px rgba(0,0,0,0.03)", marginBottom: 24, border: "1px solid #e5e7eb" }}>
              <div style={{ background: "#0f5f3b", color: "#fff", fontWeight: 700, fontSize: 20, padding: "16px 24px", borderTopLeftRadius: 8, borderTopRightRadius: 8 }}>Class Schedule</div>
              <div style={{ padding: "24px 32px" }}>
                <div style={{ background: "#fff", padding: 18, borderRadius: 8, border: "1px solid #eee", minHeight: 220 }}>
                  {loading ? (
                    <div style={{ color: "#666" }}>Loading class schedule...</div>
                  ) : error ? (
                    <div style={{ color: "red" }}>{error}</div>
                  ) : !Array.isArray(courses) || courses.length === 0 ? (
                    <div style={{ color: "#666" }}>Please wait for your Class Schedule.</div>
                  ) : (
                    <div>
                      <div style={{ width: "100%", overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
                        <table style={{ width: "100%", minWidth: 820, borderCollapse: "collapse", marginTop: 12 }}>
                          <thead>
                            <tr style={{ background: "#f8fafb" }}>
                              <th style={{ padding: "10px 8px", borderBottom: "1px solid #e5e7eb", textAlign: "center", whiteSpace: "nowrap" }}>Subject Code</th>
                              <th style={{ padding: "10px 8px", borderBottom: "1px solid #e5e7eb", textAlign: "left", whiteSpace: "nowrap" }}>Subject Description</th>
                              <th style={{ padding: "10px 8px", borderBottom: "1px solid #e5e7eb", textAlign: "center", whiteSpace: "nowrap" }}>Section</th>
                              <th style={{ padding: "10px 8px", borderBottom: "1px solid #e5e7eb", textAlign: "center", whiteSpace: "nowrap" }}>Day</th>
                              <th style={{ padding: "10px 8px", borderBottom: "1px solid #e5e7eb", textAlign: "center", whiteSpace: "nowrap" }}>Schedule</th>
                              <th style={{ padding: "10px 8px", borderBottom: "1px solid #e5e7eb", textAlign: "center", whiteSpace: "nowrap" }}>Room</th>
                              <th style={{ padding: "10px 8px", borderBottom: "1px solid #e5e7eb", textAlign: "center", whiteSpace: "nowrap" }}>Instructor</th>
                              <th style={{ padding: "10px 8px", borderBottom: "1px solid #e5e7eb", textAlign: "right", whiteSpace: "nowrap" }}>Units</th>
                            </tr>
                          </thead>
                          <tbody>
                            {Object.values(
                              (enrichedCourses || []).reduce((acc, course) => {
                                const key = [course.courseId, course.courseName, course.section].join("|");
                                if (!acc[key]) acc[key] = {
                                  courseId: course.courseId,
                                  courseName: course.courseName,
                                  section: course.section,
                                  units: course.units,
                                  meetings: []
                                };
                                acc[key].meetings.push({
                                  day: course.day,
                                  time: course.time,
                                  room: course.room,
                                  prof: course.profName || course.prof
                                });
                                return acc;
                              }, {})
                            ).map((row) => (
                              <tr key={row.courseId + "-" + row.section} style={{ borderBottom: "1px solid #e5e7eb" }}>
                                <td style={{ padding: "8px", verticalAlign: "top", textAlign: "center", wordBreak: "break-word" }}>{row.courseId}</td>
                                <td style={{ padding: "8px", verticalAlign: "top", wordBreak: "break-word" }}>{row.courseName}</td>
                                <td style={{ padding: "8px", verticalAlign: "top", textAlign: "center", wordBreak: "break-word" }}>{row.section}</td>
                                <td style={{ padding: "8px", verticalAlign: "top", textAlign: "center", wordBreak: "break-word" }}>
                                  {row.meetings.map((m, i) => <div key={i} style={{ whiteSpace: "pre-line" }}>{m.day}</div>)}
                                </td>
                                <td style={{ padding: "8px", verticalAlign: "top", textAlign: "center", wordBreak: "break-word" }}>
                                  {row.meetings.map((m, i) => <div key={i} style={{ whiteSpace: "pre-line" }}>{m.time}</div>)}
                                </td>
                                <td style={{ padding: "8px", verticalAlign: "top", textAlign: "center", wordBreak: "break-word" }}>
                                  {row.meetings.map((m, i) => <div key={i}>{m.room}</div>)}
                                </td>
                                <td style={{ padding: "8px", verticalAlign: "top", textAlign: "center", wordBreak: "break-word" }}>
                                  {row.meetings.map((m, i) => <div key={i}>{m.prof}</div>)}
                                </td>
                                <td style={{ padding: "8px", verticalAlign: "top", textAlign: "right", wordBreak: "break-word" }}>{row.units}</td>
                              </tr>
                            ))}
                            <tr>
                              <td colSpan={7} style={{ border: "1px solid #e5e7eb", padding: 8, fontWeight: "bold", textAlign: "right" }}>TOTAL UNITS</td>
                              <td style={{ border: "1px solid #e5e7eb", padding: 8, fontWeight: "bold", textAlign: "right" }}>
                                {Object.values(
                                  courses.reduce((acc, course) => {
                                    const key = [course.courseId, course.courseName, course.section].join("|");
                                    if (!acc[key]) acc[key] = { units: parseFloat(course.units) || 0 };
                                    return acc;
                                  }, {})
                                ).reduce((sum, row) => sum + (parseFloat(row.units) || 0), 0).toFixed(1)}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
